<template>
<b-container fluid>
  <b-row>
    <b-col>
      <h3>BOM/KIT Recipe</h3>
    </b-col>
  </b-row>
  <b-row class="my-2">
    <b-col>
      <b-form-input placeholder="Part/Product Num (exact)" v-model=num @keydown.enter.native="search" />
    </b-col>
    <b-col cols=3>
      <b-input-group append="ea.">
        <b-form-input placeholder="Quantity" type=number v-model=qty @keydown.enter.native="search" />
      </b-input-group>
    </b-col>
    <b-col cols=1>
      <b-btn @click=search variant=primary> Search</b-btn>
    </b-col>
  </b-row>
  <b-row v-if=error>
    <b-col>
      Something seems to have done wrong. Details have been logged to the console, so if trying again doesn't fix it, please fetch an IT person without refreshing this page.
    </b-col>
  </b-row>
  <b-row v-else>
    <b-col cols=8>
      <center v-if=loading>
        <fingerprint-spinner
          :animation-duration="1500"
          :size="300"
          color="#293a93"
          class="my-3"
        />
      </center>
      <div v-else-if="recipe !== null">
        <b-row style="background-color: gainsboro;">
          <b-col cols=2>
            Part/Product Num (KIT|BOM|INDV)
          </b-col>
          <b-col>
            Part/Product Description
          </b-col>
          <b-col cols=3>
            Needed For 1 | Needed Total
          </b-col>
          <b-col cols=2>
            POs for item
          </b-col>
        </b-row>
        <bom-kit-cookbook-item :item=recipe :qty=recipe_qty />
      </div>
    </b-col>
    <b-col v-if="recipe !== null">
      <div>
        What does this mean?
        <b-button size="sm" variant="link" v-b-modal.info-modal class="px-0">
          <i class="fas fa-question-circle"></i>
        </b-button>
      </div>
      <b-list-group>
        <b-card header="Ingredients" class="my-2" no-body>
          <b-row>
            <b-col>
              <b-table :fields="['num', 'available', 'need', 'remaining', 'unit']" :items="traversal.raw_items">
                <template slot='num' slot-scope='data'>
                  {{data.item.product_id == null ? data.item.part_num : data.item.product_num}}
                </template>
                <template slot='available' slot-scope='data'>
                  {{parseFloat(data.item.available.toFixed(2))}}
                </template>
                <template slot='need' slot-scope='data'>
                  {{parseFloat(data.item.qty.toFixed(2))}}
                </template>
                <template slot='remaining' slot-scope='data'>
                  <div v-if="parseFloat(data.item.available-data.item.qty) > 0">{{parseFloat((data.item.available-data.item.qty).toFixed(2))}}</div>
                  <div style="background-color: rgba(255,0,0,.3" v-else>{{parseFloat((data.item.available-data.item.qty).toFixed(2))}}</div>
                </template>
                <template slot='unit' slot-scope="data">
                  {{ data.item.uomname }}
                </template>
              </b-table>
            </b-col>
          </b-row>
        </b-card>
        <b-card header="Leftovers" class="my-2" no-body v-if="traversal.byproducts.length > 0">
          <b-row>
            <b-col>
              <b-table :fields="['num', 'available', 'surplus', 'total', 'unit']" :items="traversal.byproducts">
                <template slot='num' slot-scope='data'>
                  {{data.item.product_id == null ? data.item.part_num : data.item.product_num}}
                </template>
                <template slot='available' slot-scope='data'>
                  {{parseFloat(data.item.available.toFixed(2))}}
                </template>
                <template slot='surplus' slot-scope='data'>
                  {{parseFloat(data.item.qty.toFixed(2))}}
                </template>
                <template slot='total' slot-scope='data'>
                  <div v-if="parseFloat(data.item.available+data.item.qty) > 0">{{parseFloat((data.item.available+data.item.qty).toFixed(2))}}</div>
                  <div style="background-color: rgba(255,0,0,.3" v-else>{{parseFloat((data.item.available+data.item.qty).toFixed(2))}}</div>
                </template>
                <template slot='unit' slot-scope="data">
                  {{ data.item.uomname }}
                </template>
              </b-table>
            </b-col>
          </b-row>
        </b-card>
      </b-list-group>
    </b-col>
  </b-row>
  <b-modal id='info-modal' centered scrollable hide-footer size="lg" title="More Info">
    <h3>Ingredients:</h3>
    <div class="pl-4">
      These are the "lowest" (items that aren't built) raw goods used<br/><br/>
      <strong>Available:</strong> Fishbowl's "Available For Sale" value for item listed<br/>
      <strong>Need:</strong> How many (in total) needed to build Qty {{qty}} of {{num}}<br/>
      <strong>Remaining:</strong> [Available] - [Need] => how many items would remain available for sale after building<br/>
    </div>
    <h3>Leftovers</h3>
    <div class='pl-4'>
      All the things that remain after building<br/>
      <em>Don't see this section? Your recipe may not have any leftovers!</em><br/><br/>
      <strong>Available:</strong> Fishbowl's "Available For Sale" value for item listed<br/>
      <strong>Surplus:</strong> How many (in total) of this item will remain after building Qty {{qty}} of {{num}}<br/>
      <strong>Total:</strong> [Available] + [Surplus] => how many items would be available for sale after building<br/>
    </div>
  </b-modal>
</b-container>
</template>

<script>
import axios from 'axios';
import BomKitCookbookItem from './BomKitCookbookItem'
import { FingerprintSpinner } from 'epic-spinners';
const BASE_API = process.env.VUE_API_ROOT;

export default ({
  components: {
    BomKitCookbookItem,
    FingerprintSpinner
  },
  data() {
    return {
      num: "",
      qty: 1,

      loading: false,
      error: false,

      traversal: null,
      recipe: null,
      recipe_qty: undefined,
    };
  },
  methods: {
    search() {
      if (this.num == "" || this.qty < 1) return;

      this.reset();
      axios.get(`${BASE_API}/fishbowl/reports/bom-kit-cookbook?num=${this.num}&qty=${this.qty}`).then(response => {
        this.recipe = response.data.recipe
        this.traversal = response.data.traversal
        this.recipe_qty = this.qty;
      }).catch(error => {
        console.error(error);
        this.error = true;
      }).finally(() => {
        this.loading = false;
      })
    },
    reset() {
      this.loading = true;
      this.error = false;
      this.traversal = [];
      this.recipe = null;
    }
  },
})
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/purchasing/BomKitRecipe.vue